import React,{ useState, useEffect } from 'react';
import { inject, observer } from "mobx-react";
import _useTranslate from '../../hooks/_useTranslate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GreenSubmitButton from '../../buttons/greenSubmitButton';
import LoadingOrderModal, { vivaCommitStage } from '../../modals/loadingOrderModal';
import OrderCompleteModal from '../../modals/orderCompleteModal';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { toJS } from 'mobx';
import {DEFAULT_EMAIL} from "../../assets/globalConstants";
import "./Checkout.css";

const Success = ({ feathersStore })=> {

  const greenColored = {color: "hsl(151, 100%, 28%)", fontSize: 42};
  let common = _useTranslate(feathersStore.language, "common"); 
  let navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setLoading] = useState(false);
  const [isComplete, setComplete] = useState(false); 
  //true when Viva could not confirm the charge yet (lookup failed, or still pending): nothing is committed and
  //the redirect snapshot is kept, so Retry re-runs this page. /viva/fail would wipe it (Fail.js).
  const [unconfirmed, setUnconfirmed] = useState(false);

  useEffect(() => {
    const asyncFn = async() => {
      await feathersStore.setObservables();    
      feathersStore.setLanguage(localStorage.getItem('Language'));
      feathersStore.fetchChatId();
      /* Rehydrate ONLY from a live redirect snapshot of a payment that has not committed yet. After a
         commit the snapshot is cleared (loadingOrderModal emptyCart) and the payment is marked, so a
         reload, a restored tab or a second open of this URL has nothing to replay: send the customer
         home instead of posting the order again and burning the loyalty reward twice - or hanging on
         the spinners. 'sending' (commit started, outcome unknown) deliberately DOES replay: the server
         dedups it, and a charged payment with no order is the worse failure. */
      let _snapshotItem = null, fdwaState = null;
      try{
        _snapshotItem = JSON.parse(localStorage.getItem('fdwa_orderItem') || 'null');
        fdwaState = JSON.parse(localStorage.getItem('fdwa_state') || 'null');
      }catch(e){ /* corrupt snapshot - treated as absent */ }
      const _commitStage = vivaCommitStage(searchParams.get("s"));
      if(_commitStage === 'committed' || _commitStage === 'done'
        || !fdwaState?.orderCode || !(_snapshotItem?.items?.length > 0)){
        console.warn('[viva/success] no live snapshot for', searchParams.get("s"), searchParams.get("t"), _commitStage);
        navigate("/", { replace: true });
        return;
      }
      feathersStore.setOrderItem(_snapshotItem);
      feathersStore.setHasLocationPermission(fdwaState?.hasLocationPermission);
      feathersStore.setCartLength(fdwaState?.cartLength);
      feathersStore.setShowCookies(fdwaState?.showCookies);
      feathersStore.setTableId(fdwaState?.tableId ?? 'eshop');
      if(fdwaState?.preOrderBooking) feathersStore.setPreOrderBooking(fdwaState.preOrderBooking); //survive the Viva redirect
      feathersStore.setRestaurantOpen(fdwaState?.restaurantOpen);
      feathersStore.setShowRestaurantMessage(fdwaState?.showRestaurantMessage);
      feathersStore.setDiscountsArray(fdwaState?.discountsArray);
      feathersStore.setCurrency(fdwaState?.currency);
      feathersStore.setOrderCode(fdwaState?.orderCode);
    }
    asyncFn();
  //MOUNT-ONLY. Depending on localStorage values made the clear in loadingOrderModal emptyCart re-fire this
  //effect and wipe the store - the reason those clears were commented out in c371901.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {  
    const asyncFn = async() => {              
      const _transactionId = searchParams.get("t");
      const orderCode = searchParams.get("s");
      //never re-record a payment whose order already committed (see the rehydrate guard above)
      const _commitStage = vivaCommitStage(orderCode);
      if(_commitStage === 'committed' || _commitStage === 'done'){ navigate("/", { replace: true }); return; }
      if(orderCode.toString().trim() === feathersStore.orderCode.toString().trim()){
        /* VERIFY BEFORE COMMITTING. `s` and `t` are only what the redirect URL says - anyone can type
           /viva/success?s=<their orderCode>&t=<anything>. The order used to be committed whatever this
           lookup answered (its failure was swallowed), so an unpaid payment order became a card-paid
           order. Viva's rule: check statusId, orderCode and amount before recording the payment. The
           server re-checks all of it on the commit (feathers-server pos/viva-checkout.js), including the
           amount; this is what keeps the customer from being told "successful payment" for nothing. */
        let lastTransaction;
        try{
          lastTransaction = await feathersStore.getEshopTransaction(_transactionId);
        }catch(error){
          console.log('[viva/success] the payment could not be confirmed yet', error);
          setUnconfirmed(true);
          return;
        }
        const _status = lastTransaction?.statusId ?? lastTransaction?.StatusId;
        if(_status === 'A'){ setUnconfirmed(true); return; }   //still pending at Viva
        //Number() on both sides: a 16-digit orderCode can pass MAX_SAFE_INTEGER, and the lookup's JSON
        //parse and Number(s) round the same digits to the same double.
        const _txCode = lastTransaction?.orderCode ?? lastTransaction?.OrderCode;
        if(_status !== 'F' || (_txCode != null && Number(_txCode) !== Number(orderCode))){
          console.warn('[viva/success] not a finished payment of this order', _status, _txCode, orderCode);
          navigate("/viva/fail");
          return;
        }
        // Record only what the card was actually charged for THIS session (its own items),
        // not the cumulative table balance. _webTotalDelta = this order's grandTotal (set in O&P;
        // absent in eshop, where total already is the session amount).
        const sessionPaid = +(feathersStore.orderItem?._webTotalDelta ?? feathersStore.orderItem.total);
        // The card was charged for THIS session's items -> mark them fully paid so the dashboard's
        // per-item paid badge (driven by item.paid_quantity) agrees with the recorded payment / unpaid=0.
        // Android sets these on card payments; the web app did not, so web-paid table items showed as
        // unpaid in the dashboard even though the bill was settled (receipt/archive still worked because
        // those key off order.unpaid, not per-item paid_quantity).
        const _paidItems = (feathersStore.orderItem?.items || []).map(it => it?._webNew
          ? { ...it, paid_quantity: +it.product_quantity, visa_quantity: +it.product_quantity }
          : it);
        feathersStore.setOrderItem({ items: _paidItems });
        if(feathersStore.tableId !== 'eshop'){
          let paidBy = feathersStore.orderItem?.paidBy || [];
          const paymentObject = {
            waiterId: feathersStore.orderItem.orderKind,
            branch: feathersStore?.selectedBranch,
            paidSum: sessionPaid,
            method: "Visa",
            createdAt: new Date().getTime(),
            // Key this ledger row off THIS charge, not off the table session. The fallback in
            // loadingOrderModal minted `${activeUid}-pb${index}`, and activeUid lives for the whole
            // table session while index restarts at 0 every round — so a second card payment on the
            // same table re-sent the FIRST payment's key, the server dedup dropped it, and the
            // balance was never credited (the payments[] leg still landed, keyed by transactionId,
            // so the money showed but the order stayed unpaid). Suffixed because paidBy and payments
            // share one dedup namespace server-side and the payments leg uses the bare transactionId.
            _clientPayKey: `${_transactionId || orderCode}-pb`,
            _webNew: true
          }
          paidBy.push(paymentObject);
          feathersStore.setOrderItem({          
            paidBy
          });
        }
        let payments = feathersStore.orderItem?.payments || [];
        let payment = {
          PaymentMethodType : "Card",
          PaymentMethodTypeCode : "7",
          PaymentAmount : sessionPaid,
          PaymentNotes : "Card",
          transactionId : _transactionId,
          tipAmount : feathersStore.orderItem?.tipAmount || 0,
          _webNew : true
        }
        Object.assign(payment, {extraData: lastTransaction}) //informational: the server replaces it with its own lookup
        payments.push(payment);
        feathersStore.setOrderItem({          
          payments
        });
        //---> RECURRING START

        if(feathersStore.orderItem.allowRecurring 
          && feathersStore?.user?.email !== DEFAULT_EMAIL){
          const subscription = {
            transactionId: _transactionId,
            customerId: feathersStore.user._id,
            createdAt: new Date().getTime(),
            interval: feathersStore.orderItem.subscriptionInterval,
            amount: feathersStore.orderItem.total,
            customerFirstName: feathersStore.user.firstName,
            customerLastName: feathersStore.user.lastName,
            description: feathersStore.orderItem.customerTrns ,
            cardNumber: lastTransaction?.CardNumber || lastTransaction?.cardNumber || "",
            cardExpirationDate: lastTransaction?.CardExpirationDate || lastTransaction?.cardExpirationDate || ""      
          }          
          //A replay of this page (a 'sending' re-send) must not register the same card twice.
          let subsriptionsArr = toJS(feathersStore.user?.subscriptions ?? [])
            .filter(s => !(_transactionId && s?.transactionId === _transactionId));
          subsriptionsArr.push(subscription);            
          feathersStore._patchUser(feathersStore.user._id, {subscriptions: subsriptionsArr});
        }

        //<----RECURRING END        
        
        setLoading(true);
        
      }else{
        navigate("/viva/fail") ;
      }      
    }
    
    if((searchParams.get("s")?.length > 0) && feathersStore?.orderCode &&
      (feathersStore.orderItem.items?.length > 0) && (feathersStore?.tableId?.length > 0) &&
      feathersStore?.settings?.masterLanguage){        
        asyncFn(); 
      }

  }, [feathersStore?.settings?.masterLanguage , searchParams, feathersStore.orderItem?.items?.length, 
      feathersStore?.orderCode, feathersStore?.tableId])
 
  
  //----------------- LoadingOrderModal controls ----------------- START

  const whenError = (error) => {
    setLoading(!isLoading); 
    //The server verifies the charge at Viva before it books the order (feathers-server pos/viva-checkout.js).
    //402: Viva said no - nothing was paid for this order. 409/503: not confirmed YET - keep the snapshot
    //(the commit stage stays 'sending', which replays) and offer Retry.
    if(error?.code === 402){ navigate("/viva/fail"); return; }
    if(error?.code === 409 || error?.code === 503 || error?.data?.retryable) setUnconfirmed(true);
  }

  const CompleteOrder = ()=> {
    setLoading(!isLoading);
    setComplete(!isComplete);     
  }

  const setOrder = ()=> {
    setComplete(!isComplete);      
    navigate("/", { replace: true }) ;     
  } 
  
  //----------------- LoadingOrderModal controls -----------------END

  // Same zero-total rule as loadingOrderModal's send gate: a legitimately FREE order (loyalty
  // reward / voucher covering the whole cart) must still reach the confirmation screen. The old
  // `feathersStore.orderItem?.total &&` test both withheld the modal AND rendered a stray "0".
  const _orderTotal = feathersStore.orderItem?.total;
  const hasOrderTotal = _orderTotal !== null && _orderTotal !== undefined && _orderTotal !== ""
    && Number.isFinite(+_orderTotal);

  /* Viva ALWAYS appends ?s=<orderCode>&t=<transactionId> when it redirects here, so the
     absence of `s` is a synchronous, race-free proof that this visit is not a payment
     return - a bookmark, a back-navigation, a shared link, an uptime check. It is the only
     such proof available: orderCode, orderItem, tableId and settings all hydrate
     asynchronously from localStorage and the socket, so none of them can distinguish
     "no payment" from "not loaded yet", and reading them here would break the real flow.

     Without this the page rendered its success theatre to anyone who landed on the bare
     URL - the heading "Successful payment" with a green tick against Payment, then
     Confirmation and Send-order spinners that never resolve, because the effect below is
     correctly gated on the same `s` and so never flips isLoading/isComplete. It asserted a
     payment that had not happened and then hung. Send them home instead; /viva/fail would
     be a different lie. */
  if(!(searchParams.get("s")?.length > 0)) return <Navigate to="/" replace/>;

  if(unconfirmed) return(
    <div className="body-container">
      <div className="grey-header">{common.paymentUnconfirmedTitle || "Payment not confirmed yet"}</div>
      <div className="success-container">
        <div className="success-line">
          <div className="first-phrase">{common.paymentUnconfirmed ||
            "We could not confirm your payment with the bank yet, so your order has not been sent. Retrying only checks again - it will not charge your card."}</div>
        </div>
        <div className="success-line">
          <GreenSubmitButton onClick={() => window.location.reload()} width="300px"
            title={common.tryAgain || "Try again"}/>
        </div>
      </div>
    </div>
  );

  return(
    <>
    <div className="body-container">
      <div className="grey-header">{common.successfulPayment}</div>
      <div className="success-container">
        <div className="success-line">
          <div className="first-phrase">{common.payment}</div>          
          <div className="success-mui-img" ><CheckCircleIcon sx={greenColored}/></div>
        </div>
        <div className="success-line">
          <div className="first-phrase">{common.confirmation}</div>
          {
            isLoading ?
            <div className="success-mui-img" ><CheckCircleIcon sx={greenColored}/></div>
            :
            <img className="success-img" src={require("../../img/200_spinner.gif")}/>
          }
          
        </div>
        <div className="success-line">
          <div className="first-phrase">{common.sendOrder}</div>
          {
            isComplete ?
            <div className="success-mui-img" ><CheckCircleIcon sx={greenColored}/></div>
            :
            <img className="success-img" src={require("../../img/200_spinner.gif")}/>
          }
        </div>
      </div> 
    </div>
    {hasOrderTotal &&
     <LoadingOrderModal
        visible={isLoading}
        whenSent={CompleteOrder}
        whenError={whenError}
        total={(+feathersStore.orderItem?.total)}
        commitKey={searchParams.get("s")}
      />
    }
    
      <OrderCompleteModal
        visible={isComplete}
        onClickFinish={setOrder}        
      />
    </>
  )
}

export default inject('feathersStore')(observer(Success));